import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Divider, Paper, Typography, useTheme } from '@mui/material';
import type { InsightListData } from '../../types';
import StatusBadge from './StatusBadge';
import TileMetaActions from '../TileMetaActions';

type Props = { data: InsightListData };

export default function InsightListRenderer({ data }: Props) {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        width: '100%',
        position: 'relative',
      }}
    >
      <TileMetaActions query={data.meta?.query} registry={data.meta?.registry} />
      {data.items.map((item, index) => {
        const Icon =
          item.iconVariant === 'clock'
            ? AccessTimeIcon
            : item.iconVariant === 'trend'
              ? TrendingUpIcon
              : WarningAmberIcon;
        const iconColor =
          item.iconVariant === 'trend'
            ? theme.palette.success.main
            : theme.palette.warning.main;

        return (
          <Box key={item.title}>
            <Box
              sx={{
                px: 2.5,
                py: 2,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0, alignItems: 'flex-start', flex: 1 }}>
                <Icon sx={{ color: iconColor, fontSize: 22, mt: 0.15, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {item.title}
                  </Box>
                  {item.description ? (
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {' '}
                      — {item.description}
                    </Box>
                  ) : null}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  flexShrink: 0,
                  gap: 0.75,
                  textAlign: 'right',
                }}
              >
                {item.affectsLabel ? (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {item.affectsLabel}
                  </Typography>
                ) : null}
                {item.category ? (
                  <StatusBadge badge={{ label: item.category, tone: 'neutral' }} />
                ) : null}
              </Box>
            </Box>
            {index < data.items.length - 1 ? <Divider /> : null}
          </Box>
        );
      })}
    </Paper>
  );
}
